import React, { useMemo, useEffect, useState, useRef } from "react";
import CardBalance from "@/components/card-balance/card-balance";
// import { TabContainer } from "@/components/tab-container/tab-container";
import { GridCards } from "@/components/grid-cards/grid-cards";
import BenefitCard from "@/components/benefit-card/benefit-card";
import SectionWithHeadings from "@/components/section-with-headings/section-with-headings";
import { WalletPageContainer, ContentContainer, SectionContainer, CurrenciesList, TabHeader } from "./style-component";
import CollectibleCard from "@/components/collectible-card/collectible-card";
import LibraryHeader from "@/components/library-header/library-header";
import CollectionCard from "@/components/collection-card/collection-card";
import useFetch from "@/hook/useFetch/useFetch";
import { getCollectibles } from "@/services/collectibles/collectibles";
import { getBenefitAllocations } from "@/services/benefits/benefit";
import { SpaceshipLoader } from "@/components/spaceship-loader/spaceship-loader";
import { Collectible } from "@/types/collectible/collectible-type";
import { Collection } from "@/types/collection/collection-type";
import { useUserContext } from "@/contexts/user/useUserContext";
import { BenefitAllocation } from "@/types/benefit-allocation/benefit-allocation";
import { WalletProfileBanner } from "./components/wallet-profile-banner";
import brandData from "@/assets/brand.json"
import avatarFallback from "@/assets/default-avatar.webp"
import SEOHelmet from "@/components/SEOHelmet/SEOHelmet";
import { amplitudeAddTrack, determineProjectName } from "@/utils/amplitud/event-track";

const WalletPage: React.FC = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const { user, refetchBalance } = useUserContext();
    const [ownerKey, setOwnerKey] = useState<string | null>( null);
    const firstRender = useRef(true);

    const firstRenderAmplitude = useRef<boolean>(true);
    useEffect(() => {
        if (firstRenderAmplitude.current) {
            firstRenderAmplitude.current = false;
            amplitudeAddTrack(determineProjectName(`Wallet`), {
                title: determineProjectName(`Wallet`),
            })
        }
    }, []);

    useEffect(() => {
        setOwnerKey(user?.main_key ?? null);
    }, [user]);

    useEffect(() => {
        if (ownerKey && !firstRender.current) {
            return;
        }
        firstRender.current = false;
        const fetchData = async () => {
            await refetchBalance?.();
        };
        fetchData();
    }, [ownerKey, refetchBalance]);

    const processCollectionsFromCollectibles = (collectibles: Collectible[]) => {
        const collectionsMap = new Map();

        collectibles?.forEach(collectible => {
            if (collectible.collection) {
                const collection = collectible.collection;
                if (!collectionsMap.has(collection.collection_key)) {
                    collectionsMap.set(collection.collection_key, {
                        ...collection,
                        collectibles: []
                    });
                }
                collectionsMap.get(collection.collection_key).collectibles.push(collectible);
            }
        });

        return Array.from(collectionsMap.values());
    };

    const {
        data: collectibles,
        loading: collectiblesLoading,
        startFetch: startFetchCollectibles
    } = useFetch(getCollectibles, { owner: String(ownerKey) }, { autoFetch: false });

    const collections = useMemo(() => {
        return processCollectionsFromCollectibles(collectibles || []);
    }, [collectibles]);

    const {
        data: benefits,
        loading: benefitsLoading,
        startFetch: startFetchBenefits
    } = useFetch(getBenefitAllocations, { user_main_key: String(ownerKey) }, { autoFetch: false });

    const firstRender2 = useRef(true);

    useEffect(() => {
        if (ownerKey && firstRender2.current ) {
            firstRender2.current = false;
            startFetchCollectibles();
            startFetchBenefits();

        }
    }, [ownerKey, startFetchCollectibles, startFetchBenefits]);

    const balance = user?.balance;

    const filterBenefits = (benefits: BenefitAllocation[], term: string) => {
        
        //Exclude ERC20s
        const excludedTypes = ["ERC20"]

        const includedBenefits = benefits?.filter(benefit =>
            !excludedTypes.some(type => benefit.benefit.reward.reward_type.includes(type))
        );
        
        if (!term) return includedBenefits;
        
        return includedBenefits?.filter(benefit =>
            benefit.benefit.reward.name.toLowerCase().includes(term.toLowerCase())
        );
    };

    const filterCollectibles = (collectibles: Collectible[], term: string) => {
        if (!term) return collectibles;
        return collectibles?.filter(collectible =>
            collectible.metadata?.json_data?.name?.toLowerCase().includes(term.toLowerCase())
        );
    };

    const filterCollections = (collections: Collection[], term: string) => {
        if (!term) return collections;
        return collections?.filter(collection =>
            collection.name.toLowerCase().includes(term.toLowerCase())
        );
    };

    const filteredBenefits = useMemo(() =>
        filterBenefits(benefits || [], searchTerm),
        [benefits, searchTerm]);

    const filteredCollectibles = useMemo(() =>
        filterCollectibles(collectibles || [], searchTerm),
        [collectibles, searchTerm]);

    const filteredCollections = useMemo(() =>
        filterCollections(collections || [], searchTerm),
        [collections, searchTerm]);

    const handleSearch = (value: string) => {
        setSearchTerm(value);
    };

    const tabs = [
        {
            id: 'benefits',
            label: 'Beneficios',
            'data-testid': 'benefits-tab',
            content: (
                <TabHeader data-testid="benefits-content" $gap={32}>
                    <LibraryHeader onSearch={handleSearch} onFilter={() => { }} />
                    {benefitsLoading ? (
                        <SpaceshipLoader height="200px" />
                    ) : (
                        <GridCards
                            items={filteredBenefits || []}
                            variant="benefit"
                            renderItem={(benefit: BenefitAllocation) => (
                                <BenefitCard
                                    benefit={benefit.benefit}
                                    expandable={true}
                                    cursor={true}
                                />
                            )}
                        />
                    )}
                </TabHeader>
            )
        },
        {
            id: 'collectibles',
            label: 'Coleccionables',
            'data-testid': 'collectibles-tab',
            content: (
                <TabHeader data-testid="collectibles-content" $gap={32}>
                    <LibraryHeader onSearch={handleSearch} onFilter={() => { }} />
                    {collectiblesLoading ? (
                        <SpaceshipLoader height="200px" />
                    ) : (
                        <GridCards
                            items={filteredCollectibles || []}
                            variant="collectible"
                            renderItem={(collectible: Collectible) => (
                                <CollectibleCard
                                    thumb_url={collectible.thumb_url ?? ''}
                                    name={collectible.metadata?.json_data?.name ?? ''}
                                    token_id={collectible.token_id}
                                />

                            )}
                        />
                    )}
                </TabHeader>
            )
        },
        {
            id: 'collections',
            label: 'Colecciones',
            'data-testid': 'collections-tab',
            content: (
                <TabHeader data-testid="collections-content" $gap={32}>
                    <LibraryHeader onSearch={handleSearch} onFilter={() => { }} />
                    {collectiblesLoading ? (
                        <SpaceshipLoader height="200px" />
                    ) : (
                        <GridCards
                            items={filteredCollections}
                            variant="collection"
                            renderItem={(collection: Collection) => (
                                <CollectionCard
                                    thumb_url={collection.banner_url_collection}
                                    name={collection.name}
                                    collectibles_count={collection.collectibles?.length || 0}
                                    collectibles={(collection.collectibles || []).map(c => ({
                                        ...c,
                                        thumb_url: c.thumb_url ?? ''
                                    }))}
                                    collection_key={collection.collection_key}
                                />
                            )}
                        />
                    )}
                </TabHeader>
            )
        }
    ];

    const getUserImage = () => {
        const url = user?.wallet_pic_url;
        if (url === "randomtext" || !url) {
            return brandData.pages.wallet.avatar;
        }
        return avatarFallback;
    }

    return (
        <WalletPageContainer>
            <SEOHelmet
                metadata={{
                    title: determineProjectName(`Wallet`),
                    description: determineProjectName(`Wallet`)
                }}
            />
            <ContentContainer>
                <WalletProfileBanner
                    profileImage={getUserImage()}
                    username={user?.username ?? ''}
                />
                <SectionContainer>
                    <SectionWithHeadings title="Balance" titleColor="default">
                        <CurrenciesList>
                            {brandData.pages.wallet.currencies.map((currency) => {
                                return <CardBalance
                                    amount={balance ?? 0}
                                    details="ver historial"
                                    symbol={currency.symbol}
                                    title={currency.title}
                                    icon={currency.icon}
                                    key={currency.symbol}
                                />
                            })
                            }
                        </CurrenciesList>
                    </SectionWithHeadings>
                </SectionContainer>
                <SectionContainer>
                    <SectionWithHeadings title="Beneficios" titleColor="default">
                        {tabs[0].content}
                        {/* <TabContainer tabs={tabs} /> */}
                    </SectionWithHeadings>
                </SectionContainer>
            </ContentContainer>
        </WalletPageContainer>
    );
};

export default WalletPage;
